import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Building2, ShieldAlert, LogOut } from "lucide-react";

/**
 * Sprint 24 — /admin layout. Rejects non super admins server-side (endpoints
 * return 403) and client-side (this shell).
 */
export default function AdminLayout() {
  const { user, isSuperAdmin, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (user === false) nav("/login", { replace: true });
  }, [user, nav]);

  if (user === null) return <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>;
  if (user === false) return null;
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="admin-denied">
        <div className="max-w-md text-center space-y-3">
          <ShieldAlert className="w-10 h-10 mx-auto text-destructive" />
          <h1 className="text-xl font-semibold">Access denied</h1>
          <p className="text-sm text-muted-foreground">
            This area is restricted to RestroPilot operators. Return to your workspace.
          </p>
          <Button onClick={() => nav("/")}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const nav_items = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, exact: true },
    { to: "/admin/tenants", label: "Tenants", icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-muted/20" data-testid="admin-layout">
      <header className="border-b bg-background sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2 font-display font-bold">
            <ShieldAlert className="w-5 h-5 text-primary" />
            <span>RestroPilot Admin</span>
          </div>
          <nav className="flex items-center gap-1 flex-1 overflow-x-auto">
            {nav_items.map((n) => {
              const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase()}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${active ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`}>
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="text-xs text-muted-foreground hidden md:block">{user?.email}</div>
          <Button size="sm" variant="outline" onClick={logout} data-testid="admin-logout"><LogOut className="w-3.5 h-3.5 mr-1" /> Logout</Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
